const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://toxsee.onrender.com';
const monographWarmCache = new Map();

function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function fetchJson(url, options = {}) {
  const {
    retries = 0,
    retryDelayMs = 800,
    ...fetchOptions
  } = options;

  let response;

  try {
    response = await fetch(url, fetchOptions);
  } catch (error) {
    if (retries > 0) {
      await delay(retryDelayMs);
      return fetchJson(url, {
        ...fetchOptions,
        retries: retries - 1,
        retryDelayMs,
      });
    }

    const message = error instanceof Error ? error.message : 'Network request failed.';
    throw new Error(
      message === 'Failed to fetch'
        ? `Unable to reach the drug monograph service right now.`
        : message,
    );
  }

  const rawText = await response.text();
  let payload = {};

  try {
    payload = rawText ? JSON.parse(rawText) : {};
  } catch {
    payload = {};
  }

  if (!response.ok) {
    const textSnippet = rawText
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);

    throw new Error(
      payload?.error
      || payload?.message
      || textSnippet
      || `Request failed with status ${response.status}`,
    );
  }

  return payload;
}

export async function searchDrugProducts(query) {
  const normalized = String(query || '').trim();
  if (!normalized) {
    return [];
  }

  const payload = await fetchJson(
    `${BASE_URL}/api/drugs/search?q=${encodeURIComponent(normalized)}`,
  );

  return Array.isArray(payload?.results) ? payload.results : [];
}

export async function saveUserDrug(userId, drug) {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    throw new Error('A user identifier is required.');
  }

  return fetchJson(`${BASE_URL}/api/users/${encodeURIComponent(normalizedUserId)}/drugs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      din: drug?.din,
      productName: drug?.productName,
    }),
  });
}

export async function fetchUserDrugs(userId) {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    return [];
  }

  const payload = await fetchJson(
    `${BASE_URL}/api/users/${encodeURIComponent(normalizedUserId)}/drugs`,
  );

  return Array.isArray(payload?.drugs) ? payload.drugs : [];
}

export async function fetchDrugMonograph(din, options = {}) {
  const normalizedDin = String(din || '').trim();
  const payload = await fetchJson(
    `${BASE_URL}/api/drugs/${encodeURIComponent(normalizedDin)}/monograph/fetch`,
    {
      method: 'POST',
      retries: 1,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        forceRefresh: options.forceRefresh === true,
      }),
    },
  );

  return payload;
}

export function warmDrugMonograph(din, options = {}) {
  const normalizedDin = String(din || '').trim();
  if (!normalizedDin) {
    return Promise.resolve(null);
  }

  if (options.forceRefresh === true) {
    return fetchDrugMonograph(normalizedDin, { forceRefresh: true });
  }

  if (monographWarmCache.has(normalizedDin)) {
    return monographWarmCache.get(normalizedDin);
  }

  const request = fetchDrugMonograph(normalizedDin)
    .then((payload) => payload)
    .catch((error) => {
      monographWarmCache.delete(normalizedDin);
      throw error;
    });

  monographWarmCache.set(normalizedDin, request);
  return request;
}

export async function preloadDrugMonographs(drugs, options = {}) {
  const uniqueDrugs = [];
  const seen = new Set();

  for (const drug of Array.isArray(drugs) ? drugs : []) {
    const din = String(drug?.din || '').trim();
    if (!din || seen.has(din)) {
      continue;
    }

    seen.add(din);
    uniqueDrugs.push(drug);
  }

  const concurrency = Math.max(1, Math.min(Number(options.concurrency) || 2, 4));
  const results = [];
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < uniqueDrugs.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      const drug = uniqueDrugs[currentIndex];

      try {
        const payload = await warmDrugMonograph(drug.din, {
          forceRefresh: options.forceRefresh === true,
        });
        results[currentIndex] = { drug, payload, error: null };
        if (typeof options.onSettled === 'function') {
          options.onSettled({ drug, payload, error: null });
        }
      } catch (error) {
        results[currentIndex] = { drug, payload: null, error };
        if (typeof options.onSettled === 'function') {
          options.onSettled({ drug, payload: null, error });
        }
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, uniqueDrugs.length || 1) }, worker));
  return results;
}

export async function fetchDrugMonographMetadata(din) {
  const normalizedDin = String(din || '').trim();
  const payload = await fetchJson(
    `${BASE_URL}/api/drugs/${encodeURIComponent(normalizedDin)}/monograph`,
  );

  return payload?.monograph || null;
}

export function buildDrugMonographPdfUrl(din) {
  return `${BASE_URL}/api/drugs/${encodeURIComponent(String(din || '').trim())}/monograph/pdf`;
}

export async function askDrugQuestion(din, question) {
  const normalizedDin = String(din || '').trim();
  const payload = await fetchJson(
    `${BASE_URL}/api/drugs/${encodeURIComponent(normalizedDin)}/ask`,
    {
      method: 'POST',
      retries: 1,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question }),
    },
  );

  return payload;
}
